import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  MenuItem,
  useMediaQuery,
  Button,
  IconButton,
  Grid,
  FormHelperText,
  TextField
} from '@mui/material';
import { useSelector } from 'react-redux';
import PlaceIcon from '@mui/icons-material/Place';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CustomButton from '../../MUI_custom_component/Button/CustomButton';
// import TextField from '../../MUI_custom_component/TextField/index';
import CustomSelect from '../../MUI_custom_component/Select/CustomSelect';
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  InfoWindow,
} from '@react-google-maps/api';
import GpsFixedIcon from '@mui/icons-material/GpsFixed';
import ReusableModal from '../../MUI_custom_component/Modal/CustomModal';
import AddressConfirmationModal from './AddressConfirmationModal';
import { mapStyles } from '../mapStyles';
import { GetAllStates } from '../../../screen/auth/action/util_actions';
import {
  GetAllCityOfState,
  getLocationAndPincodeAction,
} from '../../Onboarding/auth/action';
import {
  convertAddressToCoordinates,
  reverseGeocodeAction,
} from '../APIs/action';
import { toast } from 'react-toastify';
import {
  formatCompleteAddress,
  getKeyByValue,
  getValueByKey,
} from '../../../utils/settings';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';

const LabelDisplayItem = ({ label, value }) => {
  return (
    <Grid
      item
      xs={6}
      sm={6}
    >
      <Typography
        variant='labelD'
        sx={{color: '#666666',mt: '8px' }}
      >
        {label}
      </Typography>
      <Typography
        variant='body2'
        sx={{ color: '#8C8C8C',mt:'4px' }}
      >
        {value}
      </Typography>
    </Grid>
  );
};

const AddressFormModal = ({
  onBack,
  onSaveAddress,
  addressDetails = {},
  coords = { lat: 28.6, lng: 77.1 },
  isAddressBeingEdited = false,
  companyEsiID = null
}) => {
  const { is_PWA } = useSelector((state) => ({
    is_PWA: state.loginReducer.is_PWA,
  }));

  const isMobile = useMediaQuery('(max-width: 1024px)');

  const { isLoaded, loadError } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries: ['places'],
  });

  const zoom = 15;

  const [map, setMap] = useState(null);
  const [showInfoWindow, setShowInfoWindow] = useState(false);

  const isGoogleMapsReady = isLoaded && !loadError;
  const onLoad = useCallback((map) => {
    setMap(map);
  }, []);

  const onUnmount = useCallback((map) => {
    if (
      typeof window !== 'undefined' &&
      window.google &&
      window.google.maps &&
      map
    ) {
      try {
        setMap(null);
      } catch (error) {
        setMap(null);
      }
    } else {
      setMap(null);
    }
  }, []);

  const [formData, setFormData] = useState({
    addressLine1: '',
    addressLine2: '',
    district: '',
    pincode: '',
    state: '',
  });

  const [errors, setErrors] = useState({});
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);

  const [districtOptions, setDistrictOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [pincodeOptions, setPincodeOptions] = useState([]);
  const [districtID, setDistrictID] = useState(null);
  const [locationPermissionDenied, setLocationPermissionDenied] = useState(false);

  const [mapCenter, setMapCenter] = useState({
    lat: coords?.lat,
    lng: coords?.lng,
  });

  useEffect(() => {
    if (coords && coords.lat && coords.lng) {
      setMapCenter({
        lat: Number(coords.lat),
        lng: Number(coords.lng),
      });
    } else {
      // If no coordinates provided, convert address to coordinates.
      const formattedAddress = formatCompleteAddress(addressDetails);
      if (formattedAddress) {
        getCoordinatesFromAddress(formattedAddress);
      }
    }
  }, []);

  const getCurrentLocation = (isRetry = false) => {
    if (!navigator.geolocation) {
      if (!isRetry) {
        toast.error('Geolocation is not supported by this browser.');
      }
      return;
    }

    const options = {
      enableHighAccuracy: true,
      timeout: 15000, // Increased timeout for better reliability
      maximumAge: 0, // Always request fresh location
    };

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        const newCoordinates = {
          lat: latitude,
          lng: longitude,
        };

        // Update map center to show current location
        setMapCenter(newCoordinates);

        // Fetch address details for the current location
        try {
          const response = await reverseGeocodeAction(latitude, longitude);
          if (!response.error && response.data) {
            // Update form data with current location address
            const addressData = response.data;
            setFormData({
              addressLine1: addressData.address_line_1 || '',
              addressLine2: addressData.address_line_2 || '',
              district: addressData.district_name || addressData.district || '',
              pincode: addressData.pincode_id?.toString() || addressData.pincode || '',
              state: toTitleCase(addressData.state_name || addressData.state || ''),
            });

            // Pre-populate dropdown options
            if (addressData.district_name || addressData.district) {
              setDistrictOptions([{ 
                value: addressData.district_name || addressData.district, 
                label: addressData.district_name || addressData.district 
              }]);
            }
            if (addressData.state_name || addressData.state) {
              const stateName = toTitleCase(addressData.state_name || addressData.state);
              setStateOptions(prev => {
                const stateExists = prev.some(option => option.label === stateName);
                if (!stateExists) {
                  return [...prev, { value: stateName, label: stateName }];
                }
                return prev;
              });
            }
            if (addressData.pincode_id || addressData.pincode) {
              const pincodeValue = addressData.pincode_id?.toString() || addressData.pincode;
              setPincodeOptions([{ value: pincodeValue, label: pincodeValue }]);
            }

            toast.success('Current location detected successfully!');
          }
        } catch (error) {
          console.error('Error fetching address for current location:', error);
          toast.error('Failed to fetch address for current location.');
        }

        // Reset location permission denied state
        setLocationPermissionDenied(false);
      },
      (error) => {
        if (isRetry) {
          // For retry scenarios, only show browser settings tip for permission denied
          if (error.code === error.PERMISSION_DENIED) {
            setLocationPermissionDenied(true);
            toast.info('Tip: Look for the location icon in your browser\'s address bar to manage location permissions.');
          } else {
            setLocationPermissionDenied(false);
          }
        } else {
          // For initial request, show detailed error messages
          let errorMessage = 'Unable to retrieve your location. ';

          switch (error.code) {
            case error.PERMISSION_DENIED:
              errorMessage = 'Location access was denied. Please enable location permission in your browser settings and try again.';
              setLocationPermissionDenied(true);
              break;
            case error.POSITION_UNAVAILABLE:
              errorMessage = 'Location information is unavailable. Please check your GPS settings and try again.';
              break;
            case error.TIMEOUT:
              errorMessage = 'Location request timed out. Please check your internet connection and try again.';
              break;
            default:
              errorMessage = 'An unknown error occurred while getting your location. Please try again.';
              break;
          }

          toast.error(errorMessage);
        }
      },
      options,
    );
  };

  const handleCurrentLocationRequest = () => {
    getCurrentLocation(false);
  };

  const handleRetryLocation = () => {
    // Reset permission denied state
    setLocationPermissionDenied(false);
    
    // Request location permission again (with retry flag)
    getCurrentLocation(true);
  };

  const getCoordinatesFromAddress = async (address) => {
    if (!address || address.trim().length === 0) {
      return;
    }

    try {
      const response = await convertAddressToCoordinates(address);
      const { data, error } = response;
      if (error) {
        console.error('Error converting address to coordinates:', error);
        return null;
      }

      if (data && data.latitude && data.longitude) {
        const newCoordinates = {
          lat: Number(data.latitude),
          lng: Number(data.longitude),
        };
        setMapCenter(newCoordinates);
      }
    } catch (e) {
      console.error('Error:', e);
      return null;
    }
  };

  // Ref to track if we've already initialized the options from addressDetails
  const optionsInitializedRef = useRef(false);
  const isInitializingFromAddressDetails = useRef(false);

  // Check if addressDetails has meaningful data
  const hasAddressDetails =
    addressDetails &&
    (addressDetails?.address_line_1 ||
      addressDetails?.formatted_address ||
      addressDetails?.district ||
      addressDetails?.district_name ||
      addressDetails?.pincode ||
      addressDetails?.pincode_id ||
      addressDetails?.state ||
      addressDetails?.state_name);

  // Initialize form data based on addressDetails
  useEffect(() => {
    if (hasAddressDetails) {
      isInitializingFromAddressDetails.current = true;
      setFormData({
        addressLine1: addressDetails?.address_line_1 || '',
        addressLine2: addressDetails?.address_line_2 || '',
        landmark: addressDetails?.landmark || '',
        district:
          addressDetails?.district_name || addressDetails?.district || '',
        pincode:
          addressDetails?.pincode_id?.toString() ||
          addressDetails?.pincode ||
          '',
        state: toTitleCase(
          addressDetails?.state_name || addressDetails?.state || '',
        ),
      });
    } else {
      // Reset to empty state when no address details
      isInitializingFromAddressDetails.current = false;
      optionsInitializedRef.current = false;
      setFormData({
        addressLine1: '',
        addressLine2: '',
        district: '',
        pincode: '',
        state: '',
      });
    }
  }, [addressDetails, hasAddressDetails]);

  const toTitleCase = (str) => {
    return str
      .toLowerCase()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // API calls for dropdown data
  const getStates = async () => {
    const { data, error, message } = await GetAllStates();
    if (!error) {
      setStateOptions(
        data.map((state) => ({
          label: toTitleCase(state.state_name),
          value: state.id,
        })),
      );
    }
  };

  const getDistricts = async (stateId) => {
    setDistrictOptions([]);
    setPincodeOptions([]); // Reset pincode options when state changes
    const { data, error, message } = await GetAllCityOfState(stateId);
    if (!error) {
      setDistrictOptions(
        data.map((district) => ({
          label: district.district_name,
          value: district.id,
        })),
      );
    }
  };

  const getPincodes = async (districtId) => {
    setPincodeOptions([]);
    const { data, error } = await getLocationAndPincodeAction(districtId, {
      location_id: null,
      pincode_id: null,
    });
    if (!error) {
      setPincodeOptions(
        data.pincode_list.map((pincode) => ({
          label: pincode.pincode_name,
          value: pincode.pincode_id,
        })),
      );
    }
  };

  useEffect(() => {
    getStates();
  }, []);

  // Load districts when state changes
  useEffect(() => {
    if (formData.state && !isInitializingFromAddressDetails.current) {
      getDistricts(getValueByKey(stateOptions, formData.state));
    }
  }, [formData.state]);

  // Load pincodes when district changes
  useEffect(() => {
    if (districtID && !isInitializingFromAddressDetails.current) {
      setFormData({
        ...formData,
        district: getKeyByValue(districtOptions, districtID),
      });
      getPincodes(districtID);
    }
  }, [districtID]);

  // Pre-populate options if addressDetails exist
  useEffect(() => {
    if (hasAddressDetails && !optionsInitializedRef.current) {
      // Mark as initialized to prevent re-running
      optionsInitializedRef.current = true;

      const districtName =
        addressDetails?.district_name || addressDetails?.district;
      if (districtName) {
        setDistrictOptions([{ value: districtName, label: districtName }]);
      }

      const stateName = toTitleCase(
        addressDetails?.state_name || addressDetails?.state,
      );
      if (stateName) {
        setStateOptions((prev) => {
          // Check if the state already exists to prevent duplicates
          const stateExists = prev.some((option) => option.value === stateName);
          if (!stateExists) {
            return [...prev, { value: stateName, label: stateName }];
          }
          return prev;
        });
      }

      const pincodeValue =
        addressDetails?.pincode_id?.toString() || addressDetails?.pincode;
      if (pincodeValue) {
        setPincodeOptions([{ value: pincodeValue, label: pincodeValue }]);
      }

      // Reset the initialization flag after setting options
      setTimeout(() => {
        isInitializingFromAddressDetails.current = false;
      }, 0);
    }

    // Reset the flag when addressDetails changes (e.g., when switching between different addresses)
    if (!hasAddressDetails && optionsInitializedRef.current) {
      optionsInitializedRef.current = false;
      isInitializingFromAddressDetails.current = false;
    }
  }, [hasAddressDetails]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.addressLine1.trim()) {
      newErrors.addressLine1 = 'Address Line 1 is required';
    }

    if (!districtID && !formData.district) {
      newErrors.district = 'District is required';
    }

    if (!formData.pincode) {
      newErrors.pincode = 'Pincode is required';
    } else if (!/^\d{6}$/.test(formData.pincode)) {
      newErrors.pincode = 'Pincode must be 6 digits';
    }

    if (!formData.state) {
      newErrors.state = 'State is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSaveAddress = () => {
    if (validateForm()) {
      setIsConfirmationModalOpen(true);
    }
  };

  const handleConfirmAddress = (confirmedData) => {
    setIsConfirmationModalOpen(false);
    onSaveAddress(confirmedData);
  };

  const handleCloseConfirmationModal = () => {
    setIsConfirmationModalOpen(false);
  };

  const onMapClick = (event) => {
    setShowInfoWindow(false);
  };

  const handleMarkerClick = () => {
    setShowInfoWindow(true);
  };

  const isSaveButtonDisabled = () => {
    if (hasAddressDetails) {
      // When address details exist, only check if address line 1 is filled
      return !formData.addressLine1.trim();
    } else {
      // When no address details, check all required fields
      return (
        !formData.addressLine1.trim() ||
        !formData.state ||
        (!districtID && !formData.district) ||
        !formData.pincode
      );
    }
  };

  return (
    <Box
      sx={{
        height: isMobile ? '100vh' : '589px',
        width: isMobile ? '100%' : '618px',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        position: 'relative',
        borderRadius: isMobile ? 0 : '12px',
      }}
    >
      {/* Close/Back Button */}
      <Box
        sx={{
          position: 'absolute',
          top: 16,
          left: 16,
          zIndex: 1002,
        }}
      >
        <IconButton
          onClick={onBack}
          sx={{
            backgroundColor: 'white',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
            '&:hover': {
              backgroundColor: '#f5f5f5',
            },
          }}
        >
          <ArrowBackIcon sx={{ fontSize: 24, color: '#333' }} />
        </IconButton>
      </Box>

      <Box
        sx={{
          flex: 1,
          // position: 'relative',
          // minHeight: '200px',
        }}
      >
        {isGoogleMapsReady ? (
          <GoogleMap
            key='google-map-instance'
            mapContainerStyle={{
              height: '100%',
              width: '100%',
              // position: 'relative',
              // zIndex: 1,
            }}
            center={mapCenter}
            zoom={zoom}
            onLoad={onLoad}
            onUnmount={onUnmount}
            onClick={onMapClick}
            options={{
              styles: mapStyles,
              disableDefaultUI: true,
              zoomControl: false,
              scrollwheel: false,
              disableDoubleClickZoom: true,
              gestureHandling: 'none',
            }}
          >
            <Marker
              position={mapCenter}
              draggable={false}
              icon={
                window.google && window.google.maps
                  ? {
                      url:
                        'data:image/svg+xml;charset=UTF-8,' +
                        encodeURIComponent(`
                          <svg xmlns="http://www.w3.org/2000/svg" width="120" height="120" viewBox="0 0 120 120">
                            <circle cx="60" cy="60" r="50" fill="#995A0012" stroke="#995A00" stroke-width="1"/>
                            <path d="M60 40c-5.52 0-10 4.48-10 10 0 7.5 10 20 10 20s10-12.5 10-20c0-5.52-4.48-10-10-10zm0 13.5c-1.93 0-3.5-1.57-3.5-3.5s1.57-3.5 3.5-3.5 3.5 1.57 3.5 3.5-1.57 3.5-3.5 3.5z" fill="#333333"/>
                          </svg>
                        `),
                      scaledSize: new window.google.maps.Size(120, 120),
                      anchor: new window.google.maps.Point(60, 75),
                    }
                  : undefined
              }
              onClick={handleMarkerClick}
            />

            {showInfoWindow && (
              <InfoWindow
                position={mapCenter}
                onCloseClick={() => setShowInfoWindow(false)}
              >
                <Box sx={{ textAlign: 'center', minWidth: '120px' }}>
                  <Typography
                    variant='subtitle2'
                    sx={{ fontWeight: 600, mb: 1 }}
                  >
                    📍 Selected Location
                  </Typography>
                  <Typography
                    variant='caption'
                    sx={{ display: 'block', color: '#666' }}
                  >
                    Lat: {(mapCenter?.lat || 0).toFixed(6)}
                  </Typography>
                  <Typography
                    variant='caption'
                    sx={{ display: 'block', color: '#666' }}
                  >
                    Lng: {(mapCenter?.lng || 0).toFixed(6)}
                  </Typography>
                </Box>
              </InfoWindow>
            )}
          </GoogleMap>
        ) : loadError ? (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#f5f5f5',
              flexDirection: 'column',
            }}
          >
            <Typography
              variant='body1'
              color='error'
            >
              Error loading Google Maps
            </Typography>
            <Typography
              variant='body2'
              color='text.secondary'
            >
              Please check your API key and internet connection
            </Typography>
            <Typography
              variant='body2'
              color='text.secondary'
            >
              Error: {loadError?.message || 'Unknown error'}
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: '#f5f5f5',
            }}
          >
            <Typography
              variant='body1'
              color='text.secondary'
            >
              Loading Map...
            </Typography>
          </Box>
        )}
      </Box>
      {!is_PWA && (
        <Box
          sx={{
            position: 'absolute',
            bottom: isMobile ? '560px' : '406px', // 16px above the address input section (544px mobile / 390px desktop + 16px spacing)
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1001,
          }}
        >
          <CustomButton
            variant='text'
            color='primary'
            label={
              locationPermissionDenied
                ? 'Retry Location Access'
                : 'Use Current Location'
            }
            onClick={() => {
              if (locationPermissionDenied) {
                handleRetryLocation();
              } else {
                handleCurrentLocationRequest();
              }
            }}
            sx={{
              height: '40px',
              width: '230px',
              backgroundColor: 'white',
              marginBottom: '8px',
            }}
            leftIcon={GpsFixedIcon}
          />
        </Box>
      )}

      <Box
        sx={{
          backgroundColor: 'white',
          borderTopLeftRadius: 16,
          borderTopRightRadius: 16,
          padding: '16px',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
          height: isMobile ? '544px' : '390px',
          overflowY: 'auto',
          marginTop: '-20px',
          position: 'relative',
          zIndex: 10,
        }}
      >
        {hasAddressDetails && (
          <Box>
            { addressDetails?.landmark && <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'row',
              }}
            >
              <PlaceOutlinedIcon
                sx={{ height: isMobile ? '16px' : '24px', width: isMobile ? '16px' : '24px', marginRight: '2px' }}
                color='#666666'
              />
              <Typography variant='h2'>
                {addressDetails?.landmark || 'Selected Location'}
              </Typography>
            </Box>}
            <Typography variant='body1' sx={{marginTop:'4px', }}>
              {addressDetails?.formatted_address ||
                formatCompleteAddress(addressDetails)}
            </Typography>
          </Box>
        )}

        <Box sx={{marginTop:'16px'}}>
          {/* Address Lines Row - Desktop: side by side, Mobile: stacked */}
          <Box sx={{ 
            display: isMobile ? 'block' : 'flex', 
            gap: isMobile ? 0 : '8px',
            marginBottom: '8px'
          }}>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant='labelD'
                sx={{ color: '#666666', }}
              >
                Address line 1*
              </Typography>
              <TextField
                placeholder='Enter building/house number'
                value={formData.addressLine1}
                onChange={(e) => handleInputChange('addressLine1', e.target.value)}
                fullWidth
                size='small'
                error={!!errors.addressLine1}
                helperText={errors.addressLine1}
                required
                sx={{ 
                  marginTop: '2px',
                  '& .MuiInputBase-input': {
                    fontSize: '16px',
                    fontWeight: 400,
                    fontStyle: 'normal',
                    lineHeight: '24px',
                    color: '#8C8C8C'
                  }
                }}
              />
            </Box>

            <Box sx={{ flex: 1, marginTop: isMobile ? '8px' : 0 }}>
              <Typography
                variant='labelD'
                sx={{ color: '#666666' }}
              >
                Address line 2
              </Typography>
              <TextField
                placeholder='Enter street/area name'
                fullWidth
                size='small'
                value={formData.addressLine2}
                onChange={(e) => handleInputChange('addressLine2', e.target.value)}
                sx={{ 
                  marginTop: '2px',
                  '& .MuiInputBase-input': {
                    fontSize: '16px',
                    fontWeight: 400,
                    fontStyle: 'normal',
                    lineHeight: '24px',
                    color: '#8C8C8C'
                  }
                }}
              />
            </Box>
          </Box>

          {/* State and District Row - Desktop: side by side, Mobile: stacked */}
          <Box sx={{ 
            display: isMobile ? 'block' : 'flex', 
            gap: isMobile ? 0 : '8px',
            marginBottom: '8px'
          }}>
            <Box sx={{ flex: 1 }}>
              {hasAddressDetails ? (
                <LabelDisplayItem
                  label='State*'
                  value={addressDetails?.state || formData.state}
                />
              ) : (
                <CustomSelect
                  label='State*'
                  value={formData.state}
                  disabled={hasAddressDetails}
                  error={errors.state}
                  required
                  onChange={(e) => {
                    handleInputChange('state', e.target.value);
                    // Reset district and pincode when state changes
                    setFormData((prev) => ({
                      ...prev,
                      district: '',
                      pincode: '',
                    }));
                  }}
                  fullWidth
                  sx={{ marginBottom: isMobile ? '16px' : 0 }}
                >
                  {stateOptions.map((option) => (
                    <MenuItem
                      key={option.label}
                      value={option.label}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </CustomSelect>
              )}
            </Box>

            <Box sx={{ flex: 1, marginTop: isMobile ? '8px' : 0 }}>
              {hasAddressDetails ? (
                <LabelDisplayItem
                  label='District*'
                  value={
                    addressDetails?.district_name ||
                    addressDetails?.district ||
                    formData.district
                  }
                />
              ) : (
                <CustomSelect
                  label='District*'
                  value={districtID || formData.district}
                  disabled={hasAddressDetails}
                  error={errors.district}
                  helperText={errors.district}
                  required
                  onChange={(e) => {
                    handleInputChange('district', e.target.value);
                    // Reset pincode when district changes
                    setDistrictID(e.target.value);
                    setFormData((prev) => ({
                      ...prev,
                      pincode: '',
                    }));
                  }}
                  fullWidth
                >
                  {districtOptions.map((option) => (
                    <MenuItem
                      key={option.value}
                      value={option.value}
                    >
                      {option.label}
                    </MenuItem>
                  ))}
                </CustomSelect>
              )}
            </Box>
          </Box>

          {/* <Box sx={{ marginBottom: '16px' }}>
            
          </Box> */}
          <Box sx={{ marginBottom: '16px' }}>
            {hasAddressDetails ? (
              <LabelDisplayItem
                label='Pincode*'
                value={addressDetails?.pincode || formData.pincode}
              />
            ) : (
              <CustomSelect
                label='Pincode*'
                value={formData.pincode}
                disabled={hasAddressDetails}
                error={errors.pincode}
                helperText={errors.pincode}
                required
                onChange={(e) => handleInputChange('pincode', e.target.value)}
                fullWidth
              >
                {pincodeOptions.map((option) => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </CustomSelect>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            position: 'absolute',
            bottom: 32,
            left: 0,
            right: 0,
            padding: '0 16px',
          }}
        >
          <CustomButton
            variant='filled'
            color='warning'
            label='Save Address'
            fullWidth
            disabled={isSaveButtonDisabled()}
            onClick={handleSaveAddress}
          />
        </Box>
      </Box>

      <ReusableModal
        open={isConfirmationModalOpen}
        onClose={handleCloseConfirmationModal}
        centerAlign={!isMobile}
        maxHeight={isMobile ? '100vh' : '589px'}
        topDesktop={'10%'}
        topMobile={'0%'}
        showHeader={false}
        scrollable={false}
        pt={0}
        px={0}
        pb={0}
        maxWidth={isMobile ? '100%' : '618px'}
        hideBackdrop={true}
        modalSx={{
          zIndex: 1400, // Higher than AddressFormModal
        }}
      >
        <AddressConfirmationModal
          addressData={{
            id: addressDetails?.id || null,
            address_line_1: formData.addressLine1,
            address_line_2: formData.addressLine2,
            district_name: formData.district,
            pincode_id: formData.pincode,
            phone_no: addressDetails?.phone_no,
            landmark: addressDetails?.landmark,
            state_name: formData.state,
            locationTitle:
              addressDetails?.formatted_address || 'Selected Location',
          }}
          coordinates={coords || { lat: 0, lng: 0 }}
          onConfirm={handleConfirmAddress}
          onBack={handleCloseConfirmationModal}
          isAddressBeingEdited={isAddressBeingEdited}
          companyEsiID={companyEsiID}
        />
      </ReusableModal>
    </Box>
  );
};

export default AddressFormModal;
